import type { TransactionClaimsOut } from '@/api/claimedLines';
import { StatusBadge } from '@/components/ui/status-badge';
import { claimGapAmount, claimGapState, claimSourceLabel } from '@/lib/claimedGst';
import type { Currency } from '@/lib/format';
import { formatCurrency } from '@/lib/format';

/** "Claimed GST" section for a finding's transaction — what was
 *  actually claimed on the customer's returns vs what the engine
 *  observed on the GL row (the claim-gap view, claimed-ITC design doc
 *  §4.1). Renders nothing when the customer has no claim data loaded
 *  (`periodCount === 0`) or the lookup hasn't resolved, so customers
 *  without claim ledgers see today's exact UI. */
export function ClaimedGstPanel({
  claims,
  periodCount,
  ready,
  observedTax,
  currency,
}: {
  /** The transaction's deterministic claim lines; undefined = none. */
  claims: TransactionClaimsOut | undefined;
  /** Distinct filed periods loaded for the customer (0 = no claim data). */
  periodCount: number;
  /** False until the batched lookup resolved — absence of data must not
   *  render as "nothing claimed". */
  ready: boolean;
  /** The finding's observed tax (`evidence['tax_amount']`). */
  observedTax: string | null;
  currency: Currency;
}) {
  if (!ready || periodCount === 0) return null;

  const state = claimGapState(claims, observedTax);
  const gap = claimGapAmount(claims, observedTax);
  const lines = claims?.lines ?? [];

  return (
    <section className="grid gap-1.5 rounded-md border border-border/60 bg-muted/20 px-2.5 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-slate/70">
          Claimed GST
        </p>
        <StatusBadge kind="claim" status={state} />
      </div>

      {lines.length === 0 ? (
        // Absence is information — state it, never imply it (§4.1).
        <p className="text-sm text-brand-slate">
          No claim found across {periodCount} filed{' '}
          {periodCount === 1 ? 'period' : 'periods'}.
        </p>
      ) : (
        <ul className="grid gap-1">
          {lines.map((line) => (
            <li
              key={line.claim_line_ref}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm"
            >
              <span className="font-medium text-brand-navy">{line.claimed_period}</span>
              <span className="font-mono tabular-nums text-brand-navy">
                {formatCurrency(line.claimed_tax, currency)}
              </span>
              <span className="text-xs text-brand-slate">
                {claimSourceLabel(line.claim_source_system)}
              </span>
              <span
                className="font-mono text-[11px] text-brand-slate/70"
                title={`Source line: ${line.claim_line_ref} · join: ${line.join_method}`}
              >
                {line.claim_line_ref}
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* Rendered for both branches: with no claim lines the unclaimed
          amount IS the observed tax — the reviewer needs the number, not
          just the absence statement. */}
      <p className="text-sm text-brand-slate">
        <span className="text-xs uppercase tracking-wide">Claimed total</span>{' '}
        <span className="font-mono tabular-nums text-brand-navy">
          {formatCurrency(claims?.claimed_total ?? '0', currency)}
        </span>
        {observedTax !== null && (
          <>
            {' '}
            <span className="text-xs uppercase tracking-wide">vs observed tax</span>{' '}
            <span className="font-mono tabular-nums text-brand-navy">
              {formatCurrency(observedTax, currency)}
            </span>
          </>
        )}
        {gap !== null && (
          <>
            {' '}
            <span className="text-xs uppercase tracking-wide">· unclaimed</span>{' '}
            <span className="font-mono tabular-nums text-brand-navy">
              {formatCurrency(gap, currency)}
            </span>
          </>
        )}
      </p>
    </section>
  );
}
